// Every game the shell knows about, in unlock order within each category.

import type { GameModule, Unlock } from './types';
import { getSave, totalStars, owns, type Save } from './state';

import letterLadder from '@games/words/letter-ladder';
import clueCards from '@games/words/clue-cards';
import equationBox from '@games/math/equation-box';
import patternSets from '@games/math/pattern-sets';
import rockSorter from '@games/science/rock-sorter';
import snap from '@games/fun/snap';
import numberBonds from '@games/math/number-bonds';
import factBlaster from '@games/math/fact-blaster';
import clockCoins from '@games/math/clock-coins';
import spellBridge from '@games/words/spell-bridge';
import spellingBee from '@games/words/spelling-bee';
import fillBlanks from '@games/words/fill-blanks';
import letterRain from '@games/words/letter-rain';
import balanceScale from '@games/math/balance-scale';
import placeValue from '@games/math/place-value';
import multiplicationGarden from '@games/math/multiplication-garden';
import quizDeck from '@games/science/quiz-deck';
import statesOfMatter from '@games/science/states-of-matter';
import foodWeb from '@games/science/food-web';
import hauntedHouse from '@games/fun/haunted-house';
import gemRun from '@games/fun/gem-run';
import dragonKeep from '@games/fun/dragon-keep';
import numberLineJumper from '@games/math/number-line-jumper';
import shapeLab from '@games/math/shape-lab';
import gridLogic from '@games/math/grid-logic';
import moleculeMaker from '@games/science/molecule-maker';
import natureRules from '@games/science/nature-rules';
import lightGravityLab from '@games/science/light-gravity-lab';
import measureIt from '@games/math/measure-it';
import storyProblems from '@games/math/story-problems';
import graphBuilder from '@games/math/graph-builder';
import wordBuilder from '@games/words/word-builder';
import homophoneHarbor from '@games/words/homophone-harbor';
import miniCrossword from '@games/words/mini-crossword';
import sightSprint from '@games/words/sight-sprint';
import historyTrail from '@games/science/history-trail';
import weatherWater from '@games/science/weather-water';
import simpleMachines from '@games/science/simple-machines';
import bodyMap from '@games/science/body-map';
import petArena from '@games/fun/pet-arena';
import whereIsIt from '@games/fun/where-is-it';
import fractionKitchen from '@games/math/fraction-kitchen';
import soundSets from '@games/words/sound-sets';
import dinoDig from '@games/science/dino-dig';
import skyLab from '@games/science/sky-lab';
import plantLab from '@games/science/plant-lab';
import passageQuest from '@games/words/passage-quest';
import storyMaker from '@games/words/story-maker';

export const GAMES: GameModule[] = [
  // words
  letterLadder,
  clueCards,
  spellingBee,
  fillBlanks,
  storyMaker,
  spellBridge,
  soundSets,
  letterRain,
  passageQuest,
  homophoneHarbor,
  sightSprint,
  wordBuilder,
  miniCrossword,
  // math
  equationBox,
  patternSets,
  numberBonds,
  balanceScale,
  placeValue,
  fractionKitchen,
  factBlaster,
  multiplicationGarden,
  clockCoins,
  storyProblems,
  shapeLab,
  graphBuilder,
  measureIt,
  numberLineJumper,
  gridLogic,
  // science
  rockSorter,
  quizDeck,
  plantLab,
  statesOfMatter,
  moleculeMaker,
  dinoDig,
  skyLab,
  natureRules,
  weatherWater,
  historyTrail,
  bodyMap,
  foodWeb,
  simpleMachines,
  lightGravityLab,
  // fun
  snap,
  whereIsIt,
  gemRun,
  petArena,
  hauntedHouse,
  dragonKeep,
];

export const GAME_BY_ID: Record<string, GameModule> = Object.fromEntries(GAMES.map(g => [g.id, g]));

export interface LockInfo { unlocked: boolean; needStars?: number; needGems?: number }

/**
 * Unlock tuning lives here, not in the games, so pacing can change after a playtest without
 * touching 40 files. A game's own `unlock` is the default; an entry here wins.
 */
export const UNLOCK_OVERRIDES: Record<string, Unlock> = {
  // 2026-10-25: with 42 learning games the per-game defaults (2 to 16 stars) opened nearly
  // everything by 15 stars. Spread each category so a new game lands every 3 or 4 stars and
  // the last ones open in the mid-40s. Town jobs still lend any locked game for a day.
  'words.letter-ladder': 'always', 'words.clue-cards': { stars: 3 }, 'words.spelling-bee': { stars: 5 },
  'words.fill-blanks': { stars: 7 }, 'words.story-maker': { stars: 9 }, 'words.spell-bridge': { stars: 12 },
  'words.sound-sets': { stars: 15 }, 'words.letter-rain': { stars: 18 }, 'words.passage-quest': { stars: 21 },
  'words.homophone-harbor': { stars: 24 }, 'words.sight-sprint': { stars: 27 }, 'words.word-builder': { stars: 31 },
  'words.mini-crossword': { stars: 35 },
  'math.equation-box': 'always', 'math.pattern-sets': 'always', 'math.number-bonds': { stars: 3 },
  'math.balance-scale': { stars: 6 }, 'math.place-value': { stars: 9 }, 'math.fraction-kitchen': { stars: 12 },
  'math.fact-blaster': { stars: 14 }, 'math.multiplication-garden': { stars: 17 }, 'math.clock-coins': { stars: 20 },
  'math.story-problems': { stars: 23 }, 'math.shape-lab': { stars: 26 }, 'math.graph-builder': { stars: 29 },
  'math.measure-it': { stars: 32 }, 'math.number-line-jumper': { stars: 36 }, 'math.grid-logic': { stars: 40 },
  'science.rock-sorter': { stars: 4 }, 'science.quiz-deck': { stars: 6 }, 'science.plant-lab': { stars: 9 },
  'science.states-of-matter': { stars: 12 }, 'science.molecule-maker': { stars: 15 }, 'science.dino-dig': { stars: 18 },
  'science.sky-lab': { stars: 21 }, 'science.nature-rules': { stars: 24 }, 'science.weather-water': { stars: 27 },
  'science.history-trail': { stars: 30 }, 'science.body-map': { stars: 33 }, 'science.food-web': { stars: 36 },
  'science.simple-machines': { stars: 40 }, 'science.light-gravity-lab': { stars: 44 },
};

export function unlockFor(g: GameModule): Unlock {
  return UNLOCK_OVERRIDES[g.id] ?? g.unlock;
}

export function lockInfo(g: GameModule, s: Save = getSave()): LockInfo {
  const u = unlockFor(g);
  if (u === 'always') return { unlocked: true };
  if (u.gems !== undefined) {
    return owns(g.id, s) ? { unlocked: true } : { unlocked: false, needGems: u.gems };
  }
  const need = u.stars ?? 0;
  const have = totalStars(s);
  return have >= need ? { unlocked: true } : { unlocked: false, needStars: need - have };
}

export function gamesIn(category: string): GameModule[] {
  return GAMES.filter(g => g.category === category);
}
